import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useBambooSendReceiptEmailMutation } from '@/gql/generated';
import useNetwork from '@/hooks/use-network';
import { FullScreenLoadingContext } from '@/pages/_providers/full-loading-overlay-provider';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useHandleToast } from '../../hooks/use-toast';
import TopNavigation from './top-navigation';

const formSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid Email'),
});

const EmailField = ({ id, onClose }: { id: string; onClose: () => void }) => {
  const { event: eventLoading } = useContext(FullScreenLoadingContext);
  const { isOnline } = useNetwork();
  const { showErrorToast, showSuccessToast } = useHandleToast();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const bambooSendReceiptEmailMutation = useBambooSendReceiptEmailMutation({
    onMutate: () => eventLoading.setIsLoading(true),
    onError: () => {
      showErrorToast();
    },
    onSuccess: () => {
      onClose();
      showSuccessToast('The receipt was sent to email.');
    },
    onSettled: () => {
      eventLoading.setIsLoading(false);
    },
  });
  function onSubmit(values: z.infer<typeof formSchema>) {
    bambooSendReceiptEmailMutation.mutate({
      input_obj: {
        transaction_id: id,
        receiver_email: values.email,
      },
    });
  }

  return (
    <div className="fixed z-[200] w-full h-full bg-white top-0 left-0">
      <TopNavigation onBack={onClose} />

      <div className="flex h-full flex-col w-full">
        <div className="h-full w-full flex items-center justify-center">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={clsx(
                form.formState.errors.email ? 'items-center' : 'items-end',
                'flex justify-center gap-6',
              )}
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center">
                    <FormLabel className="text-5xl mb-3 font-bold text-gray-primary">
                      Enter your Email
                    </FormLabel>
                    <div className="flex items-center gap-4">
                      <FormControl>
                        <Input
                          placeholder="Email"
                          {...field}
                          className="min-h-24 border-gray-border px-8 py-5 text-5xl font-bold placeholder:text-gray-border"
                        />
                      </FormControl>
                      <Button
                        type="submit"
                        className="min-h-24 min-w-48 text-5xl font-bold disabled:bg-gray-border"
                        disabled={!form.watch('email') || !isOnline}
                      >
                        Send
                      </Button>
                    </div>
                    <FormMessage className="text-xl text-danger-discount" />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default EmailField;
